import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { FaTimes, FaPaperPlane, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import { getAllStepGuides } from '../../../data/stepGuides';
import { useAuth } from '../../../components/router/authContext';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { logger } from '../../../utils/logger';
import './css/FeedbackModal.css';

/**
 * FeedbackModal - A modal component for submitting user feedback
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {string} [props.defaultStepId] - Default step ID to pre-select (e.g., 'routes', 'analytics', 'general')
 */
const FeedbackModal = ({
  isOpen,
  onClose,
  defaultStepId = null,
  productName = 'eFleetPlan',
  stepOptions: customStepOptions = null
}) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [selectedStepId, setSelectedStepId] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Feedback type options
  const feedbackTypes = [
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'usage_question', label: 'Usage Question or Clarification' },
    { value: 'bug_report', label: 'Bug or Error Report' },
    { value: 'data_concern', label: 'Data or Results Concern' },
    { value: 'general_comment', label: 'General Comment' }
  ];

  // Build step options from stepGuides + Analytics + General
  const stepGuides = getAllStepGuides();
  const defaultStepOptions = [
    ...Object.entries(stepGuides).map(([id, guide]) => ({
      id,
      label: guide.title
    })),
    { id: 'analytics', label: 'Analytics' },
    { id: 'general', label: 'General' }
  ];
  const stepOptions = Array.isArray(customStepOptions) && customStepOptions.length > 0
    ? customStepOptions
    : defaultStepOptions;

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset after a short delay to allow close animation
      setTimeout(() => {
        setSelectedStepId('');
        setFeedbackType('');
        setMessage('');
        setErrors({});
        setIsSuccess(false);
      }, 300);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, isSubmitting]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!selectedStepId || selectedStepId === '') {
      newErrors.selectedStepId = 'Please select an area';
    }

    if (!feedbackType) {
      newErrors.feedbackType = 'Please select a feedback type';
    }

    if (!message.trim()) {
      newErrors.message = 'Please enter your feedback message';
    } else if (message.trim().length < 10) {
      newErrors.message = 'Please provide more details (at least 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setErrors({ general: 'Please sign in to submit feedback' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Get step title for display
      const selectedStep = stepOptions.find(opt => opt.id === selectedStepId);
      const stepTitle = selectedStep ? selectedStep.label : 'Unknown';

      // Get feedback type label
      const selectedType = feedbackTypes.find(type => type.value === feedbackType);
      const feedbackTypeLabel = selectedType ? selectedType.label : feedbackType;

      // Prepare email payload
      const feedbackData = {
        step_id: selectedStepId,
        step_title: stepTitle,
        feedback_type: feedbackType,
        feedback_type_label: feedbackTypeLabel,
        message: message.trim(),
        user: {
          id: currentUser?.id || 'unknown',
          email: currentUser?.email || 'unknown',
          username: currentUser?.username || 'unknown',
          first_name: currentUser?.first_name || '',
          last_name: currentUser?.last_name || '',
          organization: currentUser?.organization || ''
        },
        context: {
          url: window.location.href,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      };

      const authToken = localStorage.getItem('authToken');
      const response = await axios.post(
        API_ENDPOINTS.contact,
        {
          name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || currentUser?.username || 'User',
          email: currentUser?.email || 'unknown@example.com',
          subject: `[${productName} Feedback] ${feedbackTypeLabel} – ${stepTitle}`,
          message: formatFeedbackEmail(feedbackData)
        },
        {
          headers: authToken ? { Authorization: `Token ${authToken}` } : {}
        }
      );

      logger.log('Feedback submitted successfully:', response.data);
      // Show success message instead of closing immediately
      setIsSuccess(true);

    } catch (error) {
      logger.error('Error submitting feedback:', error);
      setErrors({
        general: error.response?.data?.error || error.response?.data?.message || 'Failed to submit feedback. Please try again or contact support directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFeedbackEmail = (data) => {
    return `
${productName} Feedback Submission
==============================

USER INFORMATION
----------------
User ID: ${data.user.id}
Email: ${data.user.email}
Username: ${data.user.username}
Name: ${data.user.first_name} ${data.user.last_name}
Organization: ${data.user.organization || 'Not provided'}

FEEDBACK DETAILS
-----------------
Step/Area: ${data.step_title} (ID: ${data.step_id})
Feedback Type: ${data.feedback_type_label}
Timestamp: ${data.context.timestamp}

MESSAGE
-------
${data.message}

CONTEXT
-------
URL: ${data.context.url}
User Agent: ${data.context.user_agent}
    `.trim();
  };

  if (!isOpen) return null;

  // Safety check for SSR
  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div
      className="feedback-modal-overlay flex items-center justify-center p-4"
      onClick={(e) => {
        // Close on backdrop click (only if not submitting)
        if (e.target === e.currentTarget && !isSubmitting) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
    >
      {/* Backdrop */}
      <div className="feedback-modal-backdrop inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Modal */}
      <div className="feedback-modal-content bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
              <FaPaperPlane className="text-white text-lg" />
            </div>
            <h2
              id="feedback-modal-title"
              className="text-2xl font-bold text-gray-900 font-ui"
            >
              Submit Feedback
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close feedback modal"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 relative">
          {/* Success Message */}
          {isSuccess ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <div className="mb-6 flex-shrink-0 flex items-center justify-center">
                <FaCheckCircle className="text-green-600 text-4xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 font-ui">
                Thank You!
              </h3>
              <p className="text-gray-600 text-lg mb-2 font-ui">
                Your feedback has been successfully submitted.
              </p>
              <p className="text-gray-500 text-sm font-ui">
                Our team will review your feedback shortly!
              </p>
              <div className="mt-8">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-ui"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Helper text */}
              <p className="text-gray-600 text-sm mb-6 font-ui">
                Tell us what's working well and what we can improve. Your feedback goes directly to our product team at <strong>info@amptrans.net</strong>.
              </p>

              {/* General error */}
              {errors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <FaExclamationCircle className="text-red-600" />
                    <p className="font-medium font-ui">{errors.general}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step/Area Selection */}
            <div>
              <label htmlFor="step-select" className="block text-sm font-medium text-gray-700 mb-2 font-ui">
                About <span className="text-red-500">*</span>
              </label>
              <select
                id="step-select"
                value={selectedStepId}
                onChange={(e) => {
                  setSelectedStepId(e.target.value);
                  setErrors({ ...errors, selectedStepId: '' });
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-ui ${
                  errors.selectedStepId ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              >
                <option value="">Select an area...</option>
                {stepOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.selectedStepId && (
                <p className="mt-1 text-sm text-red-600 font-ui">{errors.selectedStepId}</p>
              )}
            </div>

            {/* Feedback Type Selection */}
            <div>
              <label htmlFor="feedback-type" className="block text-sm font-medium text-gray-700 mb-2 font-ui">
                Feedback Type <span className="text-red-500">*</span>
              </label>
              <select
                id="feedback-type"
                value={feedbackType}
                onChange={(e) => {
                  setFeedbackType(e.target.value);
                  setErrors({ ...errors, feedbackType: '' });
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-ui ${
                  errors.feedbackType ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              >
                <option value="">Select a feedback type...</option>
                {feedbackTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.feedbackType && (
                <p className="mt-1 text-sm text-red-600 font-ui">{errors.feedbackType}</p>
              )}
            </div>

            {/* Message Textarea */}
            <div>
              <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-2 font-ui">
                Your Feedback <span className="text-red-500">*</span>
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setErrors({ ...errors, message: '' });
                }}
                rows={6}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-ui resize-y ${
                  errors.message ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Please describe your feedback in detail..."
                disabled={isSubmitting}
              />
              {errors.message && (
                <p className="mt-1 text-sm text-red-600 font-ui">{errors.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 font-ui">
                Minimum 10 characters. Be as specific as possible to help us improve.
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium font-ui disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !isAuthenticated}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-ui disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="text-sm" />
                    <span>Submit Feedback</span>
                  </>
                )}
              </button>
            </div>
          </form>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackModal;

